import React, { useEffect, useState, useCallback } from 'react'
import QRCode from 'qrcode';
import DropdownEditMenu from '../../../components/Dashboard/DropdownEditMenu'
import { Link, useParams } from 'react-router-dom'
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import axiosClient from '../../../components/api/axiosClient';
import FormattedDate from '../../../components/Utils/FormattedDate';
import { formatPrice } from '../../../components/Utils/UtilsPricesFormat';
import EventsRecapOrdersComponent from '../../../components/Dashboard/eventsContent/EventsRecapOrdersComponent';
import DoEventActions from '../../../components/Dashboard/eventsContent/DoEventActions';
import { formatNumber, formatPerformNumber } from '../../../components/Utils/UtilsNumbersFormat';
import ShareEventModal from '../../../components/ShareEventModal';
import CommentPassShow from '../../../components/Dashboard/DropdownShowPassComment';
import ModalDeletePassComment from '../../../components/ModalDeletePassComment';
import ModalEditPassComment from '../../../components/ModalEditPassComment';
import { buildEventBookingUrl } from '../../../config/billetterie';

/** Lien d'achat public : priorité aux champs API, sinon URL billetterie Laravel (/events/{id}/paiement). */
function resolvePurchaseLink(eventData, payload) {
    const raw =
        eventData.lien_achat ??
        payload?.lien_achat ??
        null;

    if (raw != null && String(raw).trim() !== '') {
        const s = String(raw).trim();
        if (/^https?:\/\//i.test(s)) return s;
        if (s.startsWith('/') && typeof window !== 'undefined') {
            return `${window.location.origin}${s}`;
        }
        return s;
    }

    return buildEventBookingUrl(eventData.id);
}

/**
 * Logo rond au centre du QR (aligné sur QrCodeSection : 40×40 pour un QR de 220px de côté).
 * Si le logo ne charge pas ou bloque l’export (CORS), la promesse résout à false.
 */
function drawRoundedLogoOnQrCanvas(canvas, logoSrc) {
    return new Promise((resolve) => {
        if (!logoSrc || typeof document === 'undefined') {
            resolve(false);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(false);
                    return;
                }
                const w = canvas.width;
                const h = canvas.height;
                const d = Math.round((Math.min(w, h) * 40) / 220);
                const r = d / 2;
                const cx = w / 2;
                const cy = h / 2;
                const pad = Math.max(2, Math.round(d * 0.08));

                ctx.save();
                ctx.beginPath();
                ctx.arc(cx, cy, r + pad, 0, Math.PI * 2);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
                ctx.restore();

                ctx.save();
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(img, cx - r, cy - r, d, d);
                ctx.restore();
                resolve(true);
            } catch {
                resolve(false);
            }
        };
        img.onerror = () => resolve(false);
        img.src = logoSrc;
    });
}

const PURCHASE_QR_JPEG_OPTS = {
    width: 480,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: '#000000', light: '#ffffff' },
};

export default function ViewDetailsToEvents() {

    const [activeTab, setActiveTab] = useState('general'); // État pour gérer l'onglet actif
    const { id } = useParams();  // Récupérer l'ID de l'événement à partir de l'URL
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);
    const [isShareModalOpen, setShareModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [description, setDescription] = useState('');
    const [loadingBtnDesc, setLoadingBtnDesc] = useState(false);
    const [errorsDesc, setErrorsDesc] = useState('');
    const [validationError, setValidationError] = useState('');
    const [successMessage, setSuccessMessage] = useState();
    const [isDeletePassModalOpen, setDeletePassModalOpen] = useState(false);
    const [isEditPassModalOpen, setEditPassModalOpen] = useState(false);
    const [selectedPass, setSelectedPass] = useState(null);
    const [animatedViewsCount, setAnimatedViewsCount] = useState(0);
    const [copiedPurchaseLink, setCopiedPurchaseLink] = useState(false);
    const [downloadingPurchaseQr, setDownloadingPurchaseQr] = useState(false);
    const [togglingShowNbrPivotId, setTogglingShowNbrPivotId] = useState(null);

    const isShowNbrEnabled = (pivot) =>
        pivot?.show_nbr === true || pivot?.show_nbr === 1 || pivot?.show_nbr === '1';

    // Fonction pour changer l'onglet actif
    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const fetchEventDetails = async () => {
        setError(null);   // Réinitialiser les erreurs
        // Appel API pour récupérer les événements en cours de validation
        try {
            const response = await axiosClient.get(`/dashboard/event/details/e/${id}`);
            if (response.data.noContent) {
                setError(response.data); // Afficher "Aucun résultat trouvé."
            } else {
                const payload = response.data || {};
                const eventData = payload.data || {};
                // console.log(response.data);

                // Vues cumulées : l'API renvoie le total dans data.tracking.total_event_views
                const tracking = eventData.tracking || {};
                const normalizedViewsCount = Number(
                    tracking.total_event_views ??
                    eventData.total_event_views ??
                    payload.data?.tracking?.total_event_views ??
                    payload.total_event_views ??
                    0
                ) || 0;

                setDetails({
                    ...eventData,
                    total_views: normalizedViewsCount,
                    purchase_link: resolvePurchaseLink(eventData, payload),
                });
                setDescription(eventData.descrip_apk || ''); // Met à jour la description après récupération
            }
        } catch (error) {
            if (error.response && error.response.data) {
                // Récupérer les messages spécifiques de l'erreur backend
                setError(error.response.data);
            } else {
                setError({message: 'Une erreur inconnue est survenue. Veuillez réessayer plus tard.',});
            }
        } finally {
            setLoading(false); // Désactiver le spinner
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        
        if (!description.trim()) {
            setErrorsDesc('La description ne peut pas être vide.'); // Message d'erreur
            return;
        }
        setErrorsDesc('')
        setLoadingBtnDesc(true); // Indique que le bouton est en cours de traitement

        axiosClient.post(`/dashboard/event/details/update/description/e/${id}`, {descrip_apk: description})
            .then(
                ({data}) => {
                    setSuccessMessage(data.message);
                    fetchEventDetails();
                    setIsEditing(false); // Quitte le mode édition
                    setLoadingBtnDesc(false);
                    setTimeout(() => setSuccessMessage(""), 4000);

            }).catch(err => {
                const response = err.response;
                if (response) {
                    // console.log("Erreur de l'API :", response);
                    if (response.status === 422) {
                        setValidationError("Erreur ! Veuillez vérifier vos informations puis réessayer s'il vous plait !");
                    }else if (response.status === 500) {
                        if (response.data.errors) {
                            // Erreurs spécifiques aux champs
                            setErrorsDesc(response.data.errors);
                        }
                        if (response.data.message) {
                            // Erreur générale
                            setValidationError(response.data.message);
                        }
                    }
                    setLoadingBtnDesc(false);
                    setTimeout(() => setValidationError(""), 7000);
                } else {
                    setValidationError("Erreur lors de la sauvegarde.");
                    setLoadingBtnDesc(false);
                    setTimeout(() => setValidationError(""), 7000);
                }
            })
    };

    const handleCancel = () => {
        setDescription(details.descrip_apk); // Réinitialiser à la valeur initiale
        setIsEditing(false); // Quitter le mode édition
        setErrorsDesc('')
    };

    useEffect(() => {
        if (id) {
            fetchEventDetails();
        }
    }, [id]);

    useEffect(() => {
        const targetViews = Number(details?.total_views ?? 0) || 0;

        // Petite animation du compteur pour un affichage plus dynamique.
        let start = 0;
        const duration = 900;
        const stepTime = 16;
        const increment = Math.max(1, Math.ceil(targetViews / (duration / stepTime)));

        const timer = setInterval(() => {
            start += increment;
            if (start >= targetViews) {
                setAnimatedViewsCount(targetViews);
                clearInterval(timer);
                return;
            }
            setAnimatedViewsCount(start);
        }, stepTime);

        return () => clearInterval(timer);
    }, [details]);

    const handleShareClick = () => {
        setShareModalOpen(true);
    };

    const handleCopyPurchaseLink = (link) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(
            () => {
                setCopiedPurchaseLink(true);
                setTimeout(() => setCopiedPurchaseLink(false), 3000);
            },
            () => {
                setValidationError('Impossible de copier le lien.');
                setTimeout(() => setValidationError(''), 5000);
            },
        );
    };

    /** Export JPEG avec logo rond au centre (même asset que la home / QrCodeSection). */
    const handleDownloadPurchaseQr = useCallback(async () => {
        const link = details?.purchase_link;
        if (!link) return;
        setDownloadingPurchaseQr(true);
        try {
            const canvas = document.createElement('canvas');
            await QRCode.toCanvas(canvas, link, PURCHASE_QR_JPEG_OPTS);

            const imagePath = process.env.REACT_APP_IMAGE_PATH;
            const qrLogoSrc = imagePath ? `${imagePath}images/logo-events-QRoption.png` : null;
            if (qrLogoSrc) {
                await drawRoundedLogoOnQrCanvas(canvas, qrLogoSrc);
            }

            let dataUrl;
            try {
                dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            } catch {
                // Canvas « souillé » (CORS sur le logo) : réexport sans logo
                await QRCode.toCanvas(canvas, link, PURCHASE_QR_JPEG_OPTS);
                dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            }

            const safeCode = (details?.event_code || 'event').toString().replace(/[^a-zA-Z0-9-_]/g, '-');
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `qr-lien-achat-${safeCode}.jpg`;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setSuccessMessage('QR code téléchargé (JPEG).');
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch {
            setValidationError('Impossible de générer le fichier JPEG.');
            setTimeout(() => setValidationError(''), 5000);
        } finally {
            setDownloadingPurchaseQr(false);
        }
    }, [details?.purchase_link, details?.event_code]);

    const closeModal = () => {
        setShareModalOpen(false);
    };

    const handleDeletePassClick = (data) => {
        setSelectedPass(data);
        setDeletePassModalOpen(true);
    };

    const closeDeletePassModal = () => {
        setDeletePassModalOpen(false);
        setSelectedPass(null);
    };

    const handleEditPassClick = (data) => {
        setSelectedPass(data);
        setEditPassModalOpen(true);
    };

    const closeEditPassModal = () => {
        setEditPassModalOpen(false);
        setSelectedPass(null);
    };

    const handleSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(""), 5000);
        setDeletePassModalOpen(false);
        fetchEventDetails();
    };

    const handleToggleShowNbr = async (pivot, showNbr) => {
        if (togglingShowNbrPivotId === pivot.id) {
            return;
        }

        const previousValue = isShowNbrEnabled(pivot);
        setTogglingShowNbrPivotId(pivot.id);

        setDetails((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                pivots: prev.pivots.map((item) =>
                    item.id === pivot.id ? { ...item, show_nbr: showNbr } : item,
                ),
            };
        });

        try {
            const { data } = await axiosClient.post(
                `/request/user/organizer/event-official/ticket/toggle-show-nbr/pI/${pivot.id}`,
                { show_nbr: showNbr },
            );

            setSuccessMessage(data.message || 'Paramètre mis à jour.');
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            setDetails((prev) => {
                if (!prev) return prev;

                return {
                    ...prev,
                    pivots: prev.pivots.map((item) =>
                        item.id === pivot.id ? { ...item, show_nbr: previousValue } : item,
                    ),
                };
            });

            const message =
                err.response?.data?.message || 'Impossible de mettre à jour l\'affichage du stock.';
            setValidationError(message);
            setTimeout(() => setValidationError(''), 5000);
        } finally {
            setTogglingShowNbrPivotId(null);
        }
    };

    const totalViews = Number(details?.total_views ?? 0) || 0;

    if(!id) {
        return (
            <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <path
                                className="text-gray-500 co1wq"
                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                fill="currentColor"
                            />
                            <path
                                className="dark:text-gray-400 ckbo4"
                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                fill="currentColor"
                            />
                            <path
                                className="cmpw7 cdqku"
                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                    Aucun événement trouvé, impossible d'accéder à cette page.
                    </h4>
                    <Link to={'/dashboard/events/show-list'} className="btn bg-white border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc c2vpa">
                    Retour aux événements
                    </Link>
                </div>
            </div>
        )
    }

    if (loading) {
        return <LoadContentSpinner />;
    }

    // Afficher une section dédiée en cas d'erreur
    if (error) {
        return (
            <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <path
                                className="text-gray-500 co1wq"
                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                fill="currentColor"
                            />
                            <path
                                className="dark:text-gray-400 ckbo4"
                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                fill="currentColor"
                            />
                            <path
                                className="cmpw7 cdqku"
                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                    OUPS !
                    </h4>
                    <div className="mb-6 text-sm">
                    {error.message}
                    </div>
                    {error.errorCode === 'EVENT_ACCESS_DENIED' && (
                        <Link to={'/dashboard/events/show-list'} className="btn bg-white border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc c2vpa">
                        Retour aux événements
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    /** URL affichée (résolue côté client) ; largeur du champ basée sur la longueur du texte. */
    const purchaseUrl = details.purchase_link || details.lien_achat;
    const purchaseLinkInputSize = Math.min(
        Math.max(String(purchaseUrl || '').length + 3, 22),
        110
    );

    return (
        <>
            {validationError && (
                <div className="flex w-full justify-center items-center relative">

                    <div className="text-sm fixed mt-10 z-10 cf8dr cb8zv cvkq6 clbq0 cuvgf">
                        <div className="flex cm3rx ce4zk c6btv">
                            <div className="flex">
                            <svg
                                className="cbm9w czr3n cq1qg coqgc chfzq"
                                width={16}
                                height={16}
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 10.1l-1.4 1.4L8 9.4l-2.1 2.1-1.4-1.4L6.6 8 4.5 5.9l1.4-1.4L8 6.6l2.1-2.1 1.4 1.4L9.4 8l2.1 2.1z" />
                            </svg>
                            <div>{validationError}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {successMessage && (
                <div className="flex w-full justify-center items-center relative">

                    <div className="text-sm fixed mt-10 z-10 cf8dr cje53 cb8zv clbq0 cuvgf">
                        <div className="flex cm3rx ce4zk c6btv">
                            <div className="flex">
                            <svg
                                className="c612e cbm9w cq1qg coqgc chfzq"
                                width={16}
                                height={16}
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z" />
                            </svg>
                            <div>
                            {successMessage}
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="h-full translate-x-0 bg-white">
                {/* background */} 
                <div className="cecbd cvwbh cm84d cmg8v">
                    <img
                    className="cfwu1 cav8x c6btv"
                    width={979}
                    height={220}
                    alt={details.titre}
                    src={`data:image/jpeg;base64,${details.affiche}`}
                    />
                </div>
                {/* Content */} 
                <div className="cm84d cnbwt clbq0 c085h">
                     {/* Pre-header */} 
                    <div className="c3iy1 c3lsi cai6b">
                        <div className="flex items-center c2g1r c8fe8 c6o0w cetff">
                            {/* Avatar */} 
                            <div className="inline-flex c2rn6 cxx4k ccpi7 cdiog">
                            <img
                                className="rounded-lg cdnc2 cv1so cmjfg"
                                width={128}
                                height={128}
                                alt={details.titre}
                                src={`data:image/jpeg;base64,${details.affiche}`}
                            />
                            </div>
                            {/* Actions */} 
                            <div className="flex relative ch3kz cecaj">
                                <button
                                    onClick={handleShareClick}
                                    className="bg-gray-700 hover:bg-gray-800 text-white font-semibold cdj8c cg0jr ch8z9 cilvw cyn7a cnf4p inline-flex items-center rounded-lg px-3 py-1.5"
                                >
                                    <svg className="text-white mr-2" width={18} height={18} viewBox="0 0 20 20">
                                        <path fill="currentColor" d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                                    </svg>
                                    Partager
                                </button>

                                <DropdownEditMenu align="left" className="relative inline-flex bg-white border border-gray-200 rounded-lg cspbm">
                                    <li>
                                        <Link onClick={() => handleTabClick('Actions')} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3" to="#0">
                                            Modifier
                                        </Link>
                                    </li>
                                    <li>
                                        <Link className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3" to={`/dashboard/ventes/tickets-vendus/e/${details.id}`}>
                                            Voir les chiffres
                                        </Link>
                                    </li>
                                    <li>
                                        <Link className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3" to={`/dashboard/factures/events/invoices/show-details/i/${details.id}`}>
                                        Mes Factures
                                        </Link>
                                    </li>
                                    <hr />
                                    <li>
                                    <Link onClick={() => handleTabClick('Actions')} className="font-medium text-sm text-red-500 hover:text-red-600 flex py-1 px-3" to="#0">
                                        Supprimer
                                    </Link>
                                    </li>
                                </DropdownEditMenu>
                                
                                {/* <Link to={'/dashboard/events/registration'} className="bg-primary-600 hover:bg-primary-700 font-semibold cdj8c cg0jr ch8z9 cilvw cyn7a cnf4p">
                                    <svg className="cdqku cbm9w coqgc text-white" width={16} height={16} viewBox="0 0 16 16">
                                        <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                    </svg>

                                    <span className="ml-2">Nouvel événement</span>
                                </Link> */}
                            </div>
                        </div>
                    </div>
                     {/* Header */} 
                    <header className="c2bzj cydwr cai6b">
                     {/* Name */} 
                    <div className="inline-flex ce4zk c6f83">
                        <h1 className="text-gray-800 dark:text-gray-100 font-bold cbtcb">
                        {details.event_title}
                        </h1>
                    </div>
                    {/* Bio */}
                    {/* <div className="text-sm cxg65">
                        Fitness Fanatic, Design Enthusiast, Mentor, Meetup Organizer &amp; PHP
                        Lover.
                    </div> */}
                     {/* Meta */} 
                    <div className="flex flex-wrap items-center cn439">
                        <div className="flex items-center text-primary-1 font-semibold mr-3 sm:mb-0 mb-2">
                            #
                            <span
                                className="text-sm font-semibold whitespace-nowrap ml-1"
                            >
                                {details.event_code}
                            </span>
                        </div>
                        <div className="flex items-left mr-3 sm:mb-0 mb-2">
                            <svg
                                className="cmpw7 cdqku cbm9w coqgc"
                                width={16}
                                height={16}
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 8.992a2 2 0 1 1-.002-3.998A2 2 0 0 1 8 8.992Zm-.7 6.694c-.1-.1-4.2-3.696-4.2-3.796C1.7 10.69 1 8.892 1 6.994 1 3.097 4.1 0 8 0s7 3.097 7 6.994c0 1.898-.7 3.697-2.1 4.996-.1.1-4.1 3.696-4.2 3.796-.4.3-1 .3-1.4-.1Zm-2.7-4.995L8 13.688l3.4-2.997c1-1 1.6-2.198 1.6-3.597 0-2.798-2.2-4.996-5-4.996S3 4.196 3 6.994c0 1.399.6 2.698 1.6 3.697 0-.1 0-.1 0 0Z" />
                            </svg>
                            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap c1k3n ml-2">
                            {details.place}
                            </span>
                        </div>
                        <div className="flex items-center mr-3 sm:mb-0 mb-2">
                            <svg
                                className="cmpw7 cdqku cbm9w coqgc"
                                width={16}
                                height={16}
                                viewBox="0 0 16 16"
                            >
                                <path d="M3.886 1.137A8 8 0 1 1 .08 9.142a1 1 0 0 1 1.98-.284 6 6 0 1 0 3.314-6.256l.844.83a1 1 0 0 1-.53 1.698l-3.745.653a1 1 0 0 1-1.16-1.142L1.38.887A1 1 0 0 1 3.07.331l.817.806ZM9 7.586l1.707 1.707a1 1 0 1 1-1.414 1.414l-2-2A1 1 0 0 1 7 8V5a1 1 0 1 1 2 0v2.586Z" />
                            </svg>
                            <span
                                className="text-sm text-gray-500 dark:text-gray-400 cq84g c1k3n ml-2"
                            >
                                {details.default_date} . {details.hour}
                            </span>
                        </div>
                        {details.annule ? (
                            <div className="inline-flex text-[13px] rounded-full cx6qj cz4gk cydwr c1k3n cdw1w c9hxi">
                                Evénement Annulé
                            </div>
                        ) : (

                            details.status === 'Disponible' ? (
                                <div className="inline-flex text-[13px] rounded-full c1lu4 c19il cydwr c1k3n cdw1w c9hxi">
                                    {details.status}
                                </div>
                            ) : details.status === 'Disabled' ? (
                                <div className="inline-flex text-[13px] rounded-full cx6qj cz4gk cydwr c1k3n cdw1w c9hxi">
                                    Désactivé
                                </div>
                            ) : (
                                <div className="inline-flex text-[13px] rounded-full clksd cydwr c1k3n cdw1w c9hxi">
                                    {details.status}
                                </div>
                            )
                        )}
                    </div>

                    </header>

                    {/* Lien public d'achat (API ou URL générée) — copier / visiter */}
                    <div className="cai6b my-10">
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm">
                            <div className="px-4 py-3 dark:border-gray-700/80 flex items-center gap-2 rounded-t-xl overflow-hidden">
                                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <div>
                                    <h2 className="text-md font-semibold text-gray-800 dark:text-gray-100">Lien ou QR code d&apos;achat</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Copiez le lien ou
                                        ajoutez un QR code à vos affiches ou sur vos points de vente pour permettre aux acheteurs de cliquer ou le scanner pour accéder directement à votre billetterie.
                                    </p>
                                </div>
                            </div>
                            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 ml-4" />
                            <div className="p-4 rounded-b-xl">
                                {purchaseUrl ? (
                                    <div className="space-y-4">
                                        <div className="relative z-10">
                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5" htmlFor="purchase-link-input">URL du lien</label>
                                            <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                                                <div className="min-w-0 w-fit max-w-full overflow-x-auto rounded-lg">
                                                    <input
                                                        id="purchase-link-input"
                                                        readOnly
                                                        value={purchaseUrl}
                                                        size={purchaseLinkInputSize}
                                                        spellCheck={false}
                                                        className="box-border w-auto min-w-[12ch] max-w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/80 px-2 py-1.5 text-xs sm:text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-0 [field-sizing:content]"
                                                    />
                                                </div>
                                                <div className="flex items-center shrink-0 justify-end sm:justify-start self-start sm:self-center">
                                                    <DropdownEditMenu align="left" className="relative z-20 px-2 inline-flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg cspbm">
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopyPurchaseLink(purchaseUrl)}
                                                                className="w-full text-left font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-2 py-2 px-3"
                                                            >
                                                                {copiedPurchaseLink ? (
                                                                    <svg className="w-4 h-4 text-green-500 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                                                        <path d="M9.75 15.25 7 12.5 6 13.499 9.75 17.25 18 8.999 17 8z" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4 shrink-0 opacity-70" viewBox="0 0 18 18" fill="currentColor" aria-hidden>
                                                                        <path d="M14,4 C14.5522847,4 15,4.44771525 15,5 L15,12 C15,12.5522847 14.5522847,13 14,13 L13,13 L13,14 C13,14.5522847 12.5522847,15 12,15 L5,15 C4.44771525,15 4,14.5522847 4,14 L4,7 C4,6.44771525 4.44771525,6 5,6 L6,6 L6,5 C6,4.44771525 6.44771525,4 7,4 L14,4 Z M6,7 L5,7 L5,14 L12,14 L12,13 L7,13 C6.44771525,13 6,12.5522847 6,12 L6,7 Z M14,5 L7,5 L7,12 L14,12 L14,5 Z" />
                                                                    </svg>
                                                                )}
                                                                {copiedPurchaseLink ? 'Copié !' : 'Copier le texte du lien'}
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                disabled={downloadingPurchaseQr}
                                                                onClick={handleDownloadPurchaseQr}
                                                                className="w-full text-left font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 flex items-center gap-2 py-2 px-3"
                                                            >
                                                                {downloadingPurchaseQr ? (
                                                                    <svg className="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden>
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4 shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                    </svg>
                                                                )}
                                                                {downloadingPurchaseQr ? 'Génération…' : 'Télécharger le QR (JPEG)'}
                                                            </button>
                                                        </li>
                                                        <hr className="border-gray-200 dark:border-gray-700" />
                                                        <li>
                                                            <a
                                                                href={purchaseUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-medium text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-2 py-2 px-3"
                                                            >
                                                                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                </svg>
                                                                Visiter la page d&apos;achat
                                                            </a>
                                                        </li>
                                                    </DropdownEditMenu>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Aucun lien d&apos;achat n&apos;est disponible pour cet événement.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs */} 
                    <div className="cm84d cai6b">
                        <div className="cmr9m cvwbh cqdkw cq5bq c6btv cfwuq" aria-hidden="true" />
                        <ul className="text-sm flex c9zp2 cu49b c8vtj c1k3n c49xy csf8j cm84d chp98">
                            <li className="chndw cddmn cxjgq ci2ft ckjy4 cbhw6 c5ggj c3osb">
                            <button
                                className={`block ${activeTab === 'general' ? 'text-blue-500 cq84g ci0dd cp2rp' : 'text-gray-500 dark:text-gray-400'} cphv1`}
                                onClick={() => handleTabClick('general')}
                            >
                                General
                            </button>
                            </li>
                            <li className="chndw cddmn cxjgq ci2ft ckjy4 cbhw6 c5ggj c3osb">
                                <button
                                    className={`block ${activeTab === 'stats' ? 'text-blue-500 cq84g ci0dd cp2rp' : 'text-gray-500 dark:text-gray-400'} cphv1`}
                                    // href="#0"
                                    onClick={() => handleTabClick('stats')}
                                >
                                    Statistique des ventes
                                </button>
                            </li>
                            <li className="chndw cddmn cxjgq ci2ft ckjy4 cbhw6 c5ggj c3osb">
                                <button
                                    className={`block ${activeTab === 'Actions' ? 'text-blue-500 cq84g ci0dd cp2rp' : 'text-gray-500 dark:text-gray-400'} cphv1`}
                                    // href="#0"
                                    onClick={() => handleTabClick('Actions')}
                                >
                                    Actions
                                </button>
                            </li>
                        </ul>
                    </div>
                    {/* content */} 
                    {activeTab === 'general' ? (
                        <div className="flex c7k8z cqp75 cetff">
                            {/* Main content */} 
                            <div className="cvw0d cgwj2 c7jh9 cgd7w max-w-xl">
                                <div className="mb-4 rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-4 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            {/* <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Audience</p> */}
                                            <h3 className="text-gray-900 font-bold text-lg">Vues & clics cumulés</h3>
                                        </div>
                                        
                                    </div>

                                    <div className="mt-3 flex items-end justify-between">
                                        <div className="text-3xl font-extrabold text-gray-900 leading-none">
                                            {formatPerformNumber(animatedViewsCount)}
                                        </div>
                                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                                            <svg className="text-gray-600" width={20} height={20} viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M10 4c-4.5 0-8.1 2.7-9.6 5.5a1 1 0 000 1C1.9 13.3 5.5 16 10 16s8.1-2.7 9.6-5.5a1 1 0 000-1C18.1 6.7 14.5 4 10 4zm0 10c-3.7 0-6.7-2-8.1-4 1.4-2 4.4-4 8.1-4s6.7 2 8.1 4c-1.4 2-4.4 4-8.1 4zm0-6.5A2.5 2.5 0 1010 12a2.5 2.5 0 000-5z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className='cyhlg rounded-lg py-3 px-4'>
                                    <div className="text-sm cweej">
                                        <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Période de vente</h3>
                                        <span>
                                            <FormattedDate date={details.created_at} /> - {!details.cloture_vente ? (<>Non Défini</>) : (<FormattedDate date={details.cloture_vente} />)}
                                        </span>
                                    </div>
                                </div>
                                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

                                <div className="block c2hoo cm84d c6btv">
                                    <h2 className="text-gray-800 dark:text-gray-100 cgulq c6f83">
                                        Billet & Prix 
                                    </h2>
                                    {details.pivots.map((data, index) => {
                                        // Function to check if the ticket is sold out
                                        const isSoldOut = () => {
                                            const acceptedTickets = data.pivot_to_orders
                                                .filter((purchase) => purchase.status === "ACCEPTED")
                                                .reduce((sum, purchase) => sum + purchase.quantity, 0);

                                            return data.nbr_ticket === 0 && acceptedTickets === data.total_billet;
                                        };

                                        return (
                                            <div
                                                key={index}
                                                className={`border border-gray-200 cc0oq cghq3 c2vpa cb8zv cxxol c5vqk p-3 ${data.comment && 'pb-1'} mb-4`}
                                            >
                                                <div className="items-center c7nom czohk c4sak">
                                                    {/* Card */}
                                                    <div className="c6z41 cehrd c5l47 cjac8 cm4ey c7xaj">
                                                        <div className="text-sm text-gray-800 dark:text-gray-100 c1k3n">
                                                            {data.findthislabel.title}
                                                        </div>
                                                    </div>
                                                    {/* Name */}
                                                    <div className="c851m cqt5h cehrd c5l47 cjac8 c2hoo c8zuu">
                                                        <div className="text-sm text-gray-800 dark:text-gray-100 c1k3n c941w">
                                                            {formatPrice(data.prix_unitaire)}
                                                        </div>
                                                    </div>
                                                    {/* Card limits */}
                                                    <div className="clwtw cqt5h cehrd cpxid cjac8 chvik c7xaj">
                                                        <div className="text-sm">{formatNumber(data.total_billet)} &nbsp;tickets</div>
                                                    </div>
                                                    {/* Card status */}
                                                    <div className="c851m cehrd cjcps cjac8 chvik c8zuu">
                                                        <div
                                                            className={`inline-flex text-xs rounded-full text-center px-3 py-1 ${
                                                                isSoldOut() ? "bg-red-500 text-white" : "c1lu4 c19il"
                                                            }`}
                                                        >
                                                            {isSoldOut() ? "Sold Out" : "En stock"}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                            Afficher le nombre de places
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            Visible sur la page billetterie publique
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center shrink-0 gap-2">
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[3rem] text-right">
                                                            {togglingShowNbrPivotId === data.id
                                                                ? '…'
                                                                : isShowNbrEnabled(data)
                                                                  ? 'Activé'
                                                                  : 'Masqué'}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={isShowNbrEnabled(data)}
                                                            aria-label={`Afficher le nombre de places pour ${data.findthislabel?.title || 'ce billet'}`}
                                                            disabled={togglingShowNbrPivotId === data.id}
                                                            onClick={() =>
                                                                handleToggleShowNbr(
                                                                    data,
                                                                    !isShowNbrEnabled(data),
                                                                )
                                                            }
                                                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40 ${
                                                                isShowNbrEnabled(data)
                                                                    ? 'bg-primary-600'
                                                                    : 'bg-gray-200 dark:bg-gray-700'
                                                            } ${
                                                                togglingShowNbrPivotId === data.id
                                                                    ? 'opacity-60 cursor-wait'
                                                                    : 'cursor-pointer'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                                                    isShowNbrEnabled(data)
                                                                        ? 'translate-x-5'
                                                                        : 'translate-x-0.5'
                                                                }`}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>

                                                {data.comment && (

                                                    <div className="flex items-center mt-2">
                                                        <CommentPassShow align="right" comment={data.comment} />
                                                        <DropdownEditMenu align="left" className="relative inline-flex bg-white  cspbm">
                                                            
                                                            <li>
                                                                <Link onClick={() => handleEditPassClick(data)} className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3" to="#0">
                                                                    Modifier
                                                                </Link>
                                                            </li>
                                                            <hr />
                                                            <li>
                                                            <button onClick={() => handleDeletePassClick(data)} className="font-medium text-sm text-red-500 hover:text-red-600 flex py-1 px-3" to="#0">
                                                                Le Supprimer
                                                            </button>
                                                            </li>
                                                        </DropdownEditMenu>
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

                                {/* Description */} 
                                <div>
                                    <h2 className="text-gray-800 dark:text-gray-100 cgulq c6f83">
                                        Description de l'événement
                                        <button onClick={() => setIsEditing(!isEditing)} className="bg-white border border-gray-200 cc0oq cghq3 cspbm c2vpa rounded-lg c5vqk coqgc c1ga4 ml-2">
                                            <svg className="cmpw7 cdqku cbm9w" width={14} height={14} viewBox="0 0 16 16">
                                                <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                                            </svg>
                                        </button>

                                    </h2>
                                    <div className="text-sm cweej">
                                        {isEditing ? (
                                            <div className="cbw8w">
                                                <textarea
                                                id="comment"
                                                className="cn9r8 c071z w-full mb-4"
                                                rows={4}
                                                value={description}
                                                placeholder={""}
                                                onChange={(e) => setDescription(e.target.value)}
                                                required
                                                />
                                                {errorsDesc && (
                                                    <small className="text-red-600 mt-2">
                                                        {errorsDesc}
                                                    </small>
                                                )}

                                                <div className="flex flex-wrap justify-end ch3kz">
                                                    <button onClick={handleCancel} className="border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc cnf4p">
                                                        Annuler
                                                    </button>
                                                    <button 
                                                        onClick={handleSave}
                                                        disabled={loadingBtnDesc}
                                                        className="bg-primary-6000 cdj8c cg0jr ch8z9 cilvw cyn7a cnf4p hover:bg-primary-700">
                                                        {loadingBtnDesc ? (
                                                            <>
                                                                <svg
                                                                    className="animate-spin -ml-1 mr-3 h-5 w-5 ml-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <circle
                                                                        className="opacity-25"
                                                                        cx={12}
                                                                        cy={12}
                                                                        r={10}
                                                                        stroke="currentColor"
                                                                        strokeWidth={3}
                                                                    />
                                                                    <path
                                                                        className="opacity-75"
                                                                        fill="currentColor"
                                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                    />
                                                                </svg>
                                                                Sauvegarde...
                                                            </>
                                                        ) : (
                                                            <>Enregistrer</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p>
                                            {details.descrip_apk}
                                            </p>
                                        )}
                                    </div>
                                </div>


                                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />
                            </div>
                            {/* Sidebar */} 
                            <aside className="cxsb5alonge cjav5 cvgr8alonge">
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Titre</h3>
                                    {!details.event_title ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.event_title}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Code événement</h3>
                                    {!details.event_code ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.event_code}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Date de début </h3>
                                    {!details.default_date ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.default_date}</div>
                                    )}
                                </div>
                                {details.other_date && (
                                    <div className="text-sm">
                                        <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Date de fin</h3>
                                        {!details.other_date ? (
                                            <span>...</span>
                                        ) : (
                                            <div>{details.other_date}</div>
                                        )}
                                    </div>
                                )}
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Heure</h3>
                                    {!details.hour ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.hour} {'GMT'}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Lieu</h3>
                                    {!details.place ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.place}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Date de clôture des ventes</h3>
                                    {!details.cloture_vente ? (
                                        <span>...</span>
                                    ) : (
                                        <div><FormattedDate date={details.cloture_vente} /> </div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Lien GPS</h3>
                                    {!details.events_request.lien_gps ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.events_request.lien_gps}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Acteur</h3>
                                    {!details.artist ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.artist}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Catégorie</h3>
                                    {!details.eventcategorie ? (
                                        <span>...</span>
                                    ) : (
                                        <div>{details.eventcategorie.name}</div>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">Type</h3>
                                    <div className='flex'>
                                        {(details.populaire === 0 && details.vedette === 0)  ? (
                                            <span>...</span>
                                        ) : (
                                            <>
                                            {details.populaire === 1 && (
                                                <div className="flex items-center c5464 cyn7a text-xs py-1 px-2 mr-3 w-fit rounded-full ch3kz">
                                                <svg className="cveo1 cbm9w" width={14} height={14} viewBox="0 0 16 16">
                                                <path d="M10 5.934L8 0 6 5.934H0l4.89 3.954L2.968 16 8 12.223 13.032 16 11.11 9.888 16 5.934z" />
                                                </svg>
                                                {/* Rate */}
                                                <div className="text-xs cq84g c1k3n">
                                                    <span>POPULAIRE</span>
                                                </div>
                                            </div>                                          
                                            )}
                                            {details.vedette === 1 && (
                                                <div className="flex items-center c5464 cyn7a text-xs py-1 px-2 w-fit rounded-full ch3kz">
                                                    <svg className="cveo1 cbm9w" width={14} height={14} viewBox="0 0 16 16">
                                                    <path d="M10 5.934L8 0 6 5.934H0l4.89 3.954L2.968 16 8 12.223 13.032 16 11.11 9.888 16 5.934z" />
                                                    </svg>
                                                    {/* Rate */}
                                                    <div className="text-xs cq84g c1k3n">
                                                    <span>VEDETTE</span>
                                                    </div>
                                            </div>                                          
                                            )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="text-sm">
                                    <h3 className="text-gray-800 dark:text-gray-100 c1k3n">
                                        Créé le
                                    </h3>
                                    <div><FormattedDate date={details.created_at} /></div>
                                </div>
                            </aside>
                        </div>
                    ) : activeTab === 'stats' ? (
                        <EventsRecapOrdersComponent event={id} eventCode={details.event_code} />
                    ) : activeTab === 'Actions' && (
                        <DoEventActions event={id} />
                    )}
                
                </div>

            </div>
            <ShareEventModal 
                event={details} 
                isOpen={isShareModalOpen} 
                onClose={closeModal} 
            />

            <ModalEditPassComment
                dataPass={selectedPass} 
                isOpen={isEditPassModalOpen} 
                onClose={closeEditPassModal} 
                onSuccess={handleSuccess}
            />

            <ModalDeletePassComment 
                data={selectedPass} 
                isOpen={isDeletePassModalOpen} 
                onClose={closeDeletePassModal} 
                onSuccess={handleSuccess}
            />
        </>
    )
}
